import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosInstance } from 'axios';
import {
  FieldPersonnelEntity,
  FieldPersonnelState,
  FieldPersonnelStatus,
} from './types';
import { DataPayload, EntityState, SortPayloadAction } from '../types';
import FieldPersonnelApi from '../../api/fieldPersonnelApi';

const initialState: FieldPersonnelState = {
  data: [],
  currentPage: 0,
  resultsPerPage: 8,
  totalPages: 1,
  sortColumn: 'name',
  sortDirection: 1,
  selectedFilters: {},
};

const fieldPersonnelSlice = createSlice({
  name: 'fieldPersonnel',
  initialState,
  reducers: {
    setFieldPersonnel: (
      state,
      { payload }: PayloadAction<DataPayload<FieldPersonnelEntity>>
    ) => {
      state.data = payload.entities;
      state.totalPages = payload.totalPages;
    },
    sortFieldPersonnel: (
      state,
      { payload }: SortPayloadAction<FieldPersonnelEntity>
    ) => {
      state.sortColumn = payload.column;
      state.sortDirection = payload.direction;
      state.currentPage = initialState.currentPage;
      state.data = initialState.data;
    },
    setCurrentPage: (state, { payload }: PayloadAction<number>) => {
      state.currentPage = payload;
    },
    setSearchFilter: (state, { payload }: PayloadAction<string>) => {
      state.selectedFilters = {
        ...state.selectedFilters,
        search: payload,
      };
      state.currentPage = initialState.currentPage;
      state.data = initialState.data;
    },
    setStatusFilter: (
      state,
      { payload }: PayloadAction<FieldPersonnelStatus | undefined>
    ) => {
      state.selectedFilters = {
        ...state.selectedFilters,
        status: payload,
      };
      state.currentPage = initialState.currentPage;
      state.data = initialState.data;
    },
    resetPage: () => initialState,
  },
});

export const loadFieldPersonnel = async (
  axios: AxiosInstance,
  companyId?: string,
  state?: EntityState<FieldPersonnelEntity>
) => {
  if (!companyId) {
    return setFieldPersonnel({ entities: [], totalPages: 0 });
  }

  const effectiveState = state ?? initialState;

  const response = await FieldPersonnelApi.getFieldPersonnel(axios, companyId, {
    limit: effectiveState.resultsPerPage,
    page: effectiveState.currentPage,
    sortColumn: effectiveState.sortColumn,
    sortDirection: effectiveState.sortDirection as 1 | -1,
    search: effectiveState.selectedFilters?.search,
    status: effectiveState.selectedFilters?.status,
  });

  return setFieldPersonnel(response);
};

export const {
  setFieldPersonnel,
  sortFieldPersonnel,
  setCurrentPage,
  setSearchFilter,
  setStatusFilter,
  resetPage,
} = fieldPersonnelSlice.actions;

export default fieldPersonnelSlice.reducer;
